import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { isSameDay, isSameTime, differenceInMinutes } from 'owa-datetime';
import getDefaultReminder from 'owa-calendar-forms-common/lib/utils/getDefaultReminder';

export default function getEventDiffMetricProperties(
    originalEvent: CalendarEvent,
    diffEvent: CalendarEvent,
    isNewEvent: boolean,
    prefix: string
) {
    prefix = prefix ? prefix : '';

    const parentFolderIdChanged = !!diffEvent.ParentFolderId;

    let subjectSet = false;
    if (diffEvent.Subject != undefined) {
        subjectSet = true;
    }

    let validStartTimes: boolean = false;
    let validEndTime: boolean = false;
    if (originalEvent && diffEvent) {
        // '!= null' covers for both null and undefined
        validStartTimes = originalEvent.Start != null && diffEvent.Start != null;
        validEndTime = originalEvent.End != null && diffEvent.End != null;
    }

    // if it time/date are different from original event - it means that it has been set
    const startDateSet = validStartTimes ? !isSameDay(originalEvent.Start, diffEvent.Start) : false;
    const endDateSet = validEndTime ? !isSameDay(originalEvent.End, diffEvent.End) : false;
    const startTimeSet = validStartTimes
        ? !isSameTime(originalEvent.Start, diffEvent.Start)
        : false;

    const endTimeSet = validEndTime ? !isSameTime(originalEvent.End, diffEvent.End) : false;
    // Checking for presence in diffEvent is not enough, as TZ info is added with every time change
    // Check for equality instead
    const startTZChanged =
        diffEvent.StartTimeZoneId && originalEvent.StartTimeZoneId != diffEvent.StartTimeZoneId;
    const endTZChanged =
        diffEvent.EndTimeZoneId &&
        originalEvent.EndTimeZoneId != diffEvent.EndTimeZoneId &&
        // Since changing STZ auto changes ETX, only consider it an explicit action if both TZ are different
        diffEvent.StartTimeZoneId != diffEvent.EndTimeZoneId;

    let durationChanged: boolean = startTimeSet || endTimeSet;
    if (startTimeSet && endTimeSet && validStartTimes && validEndTime) {
        const originalDuration = differenceInMinutes(originalEvent.End, originalEvent.Start);
        const currentDuration = differenceInMinutes(diffEvent.End, diffEvent.Start);
        durationChanged = originalDuration != currentDuration;
    }

    let locationsSet = false;
    if (diffEvent.Locations != undefined) {
        if ((isNewEvent && diffEvent.Locations[0] != null) || !isNewEvent) {
            locationsSet = true;
        }
    }

    let allDaySet = false;
    if (diffEvent.IsAllDayEvent != undefined) {
        allDaySet = true;
    }

    let charmSet = false;
    let charmIconId: number = 0;
    if (diffEvent.CharmId != undefined) {
        if ((isNewEvent && diffEvent.CharmId != 0) || !isNewEvent) {
            charmSet = true;
            charmIconId = diffEvent.CharmId;
        }
    }

    let reminderMinutesBeforeStartSet = false;
    if (diffEvent.ReminderMinutesBeforeStart != undefined) {
        if (
            (isNewEvent && diffEvent.ReminderMinutesBeforeStart != getDefaultReminder(allDaySet)) ||
            !isNewEvent
        ) {
            reminderMinutesBeforeStartSet = true;
        }
    }

    const timeSlotChanged =
        diffEvent &&
        (diffEvent.Start != undefined ||
            diffEvent.End != undefined ||
            diffEvent.IsAllDayEvent != undefined);

    const locationsChanged = diffEvent.Locations != undefined;
    const locationsChangedCount = diffEvent.Locations != undefined ? diffEvent.Locations.length : 0;

    const requiredAttendeesChanged =
        diffEvent.RequiredAttendees != undefined && diffEvent.RequiredAttendees.length > 0;
    const optionalAttendeesChanged =
        diffEvent.OptionalAttendees != undefined && diffEvent.OptionalAttendees.length > 0;

    const requestResponsesChanged = diffEvent.IsResponseRequested != undefined;
    const allowForwardingChanged = diffEvent.DoNotForwardMeeting != undefined;
    const onlineMeetingChanged = diffEvent.IsOnlineMeeting != undefined;
    const onlineMeetingProvider = diffEvent.OnlineMeetingProvider;
    const contentChanged = diffEvent.Body != undefined;
    const showAsChanged = diffEvent.FreeBusyType != undefined;
    const privateChanged = diffEvent.Sensitivity != undefined;
    const hideAttendeesChanged = diffEvent.HideAttendees != undefined;

    const metricProperties = {
        [`${prefix}SubjectSet`]: subjectSet,
        [`${prefix}StartDateSet`]: startDateSet,
        [`${prefix}EndDateSet`]: endDateSet,
        [`${prefix}StartTimeSet`]: startTimeSet,
        [`${prefix}EndTimeSet`]: endTimeSet,
        [`${prefix}DurationChanged`]: durationChanged,
        [`${prefix}LocationsSet`]: locationsSet,
        [`${prefix}AllDaySet`]: allDaySet,
        [`${prefix}CharmSet`]: charmSet,
        [`${prefix}CharmIconId`]: charmIconId,
        [`${prefix}ReminderMinutesBeforeStartSet`]: reminderMinutesBeforeStartSet,
        [`${prefix}RequiredAttendeesChanged`]: requiredAttendeesChanged,
        [`${prefix}OptionalAttendeesChanged`]: optionalAttendeesChanged,
        [`${prefix}TimeSlotChanged`]: timeSlotChanged,
        [`${prefix}LocationsChanged`]: locationsChanged,
        [`${prefix}LocationsChangedCount`]: locationsChangedCount,
        [`${prefix}ContentChanged`]: contentChanged,
        [`${prefix}RequestResponsesChanged`]: requestResponsesChanged,
        [`${prefix}AllowForwardingChanged`]: allowForwardingChanged,
        [`${prefix}OnlineMeetingChanged`]: onlineMeetingChanged,
        [`${prefix}OnlineMeetingProvider`]: onlineMeetingProvider,
        [`${prefix}ShowAsChanged`]: showAsChanged,
        [`${prefix}PrivateChanged`]: privateChanged,
        [`${prefix}HideAttendeesChanged`]: hideAttendeesChanged,
        [`${prefix}ParentFolderIdChanged`]: parentFolderIdChanged,
        [`${prefix}StartTimeZoneSet`]: startTZChanged,
        [`${prefix}EndTimeZoneSet`]: endTZChanged,
    };

    let propertyBag = {};
    Object.keys(metricProperties).forEach(key => {
        if (metricProperties[key]) {
            propertyBag[key] = metricProperties[key];
        }
    });

    return propertyBag;
}
